import { Inject, Injectable } from '@angular/core';
import {
  AutomationReplacementChipsDataService,
  PagedResponse,
} from '../common/components/automation-replacement-chips/interfaces';
import { map, Observable, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { CampaignService, GetterCampaignData, SenderType, Statuses } from '@vendasta/campaigns';
import { shareReplay, switchMap, take } from 'rxjs/operators';
import { ListCampaignsV2Response } from '@vendasta/campaigns/lib/_internal/objects/api';
import { AUTOMATION_NAMESPACE_INJECTION_TOKEN$, AutomataContextService } from '@galaxy/automata/shared';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Context } from '@vendasta/automata';

export const ANY_CAMPAIGN = '*';

export interface SimpleCampaign {
  id: string;
  name: string;
  status: Statuses;
}

export interface Campaign {
  id?: string;
  name?: string;
  isDraft?: boolean;
  status?: string;
  createdDate?: Date;
  updatedDate?: Date;
  createdUser?: string;
  updatedUser?: string;
  recipientCampaignId?: string;
  recipientCampaignStatus?: string;
}

export interface TemplateData {
  created: string;
  fullHtml: string;
  htmlBody: string;
  name: string;
  subject: string;
  templateId: string;
  useFullHtml: boolean;
}

export interface CampaignSelectorExtraData {
  senderType?: SenderType;
  prependAnyOption?: boolean;
}

@Injectable({ providedIn: 'root' })
export class CampaignSelectorService implements AutomationReplacementChipsDataService<SimpleCampaign> {
  supportsSearching = true;

  constructor(
    private readonly translateService: TranslateService,
    private readonly automationContextService: AutomataContextService,
    @Inject(AUTOMATION_NAMESPACE_INJECTION_TOKEN$) public readonly namespace$: Observable<string>,
    private readonly campaignsService: CampaignService,
    private readonly http: HttpClient,
  ) {}
  automationContext = this.automationContextService.currentContext;

  convertDataToDisplay(c: SimpleCampaign): Observable<string> {
    return of(c.name || '');
  }

  convertDataToFormValue(c: SimpleCampaign): any {
    return c.id;
  }

  anyCampaignOption = {
    name: this.translateService.instant('AUTOMATIONS.EDITOR.TRIGGERS.CAMPAIGN_EMAIL.ANY_CAMPAIGN'),
    id: ANY_CAMPAIGN,
    status: Statuses.STATUSES_ACTIVE,
  };

  doLookup(
    searchTerm: string,
    cursor: string,
    pageSize: number,
    extraData: CampaignSelectorExtraData,
  ): Observable<PagedResponse<SimpleCampaign>> {
    return this.namespace$.pipe(
      take(1),
      switchMap((namespace) => {
        const pagingOptions = {
          cursor: cursor,
          pageSize: pageSize,
        };
        const senderType = extraData?.senderType || this.setSenderType();
        const sender = { type: senderType, id: namespace };

        return this.campaignsService.listCampaignsV2(
          sender,
          [Statuses.STATUSES_PUBLISHED, Statuses.STATUSES_ACTIVE],
          searchTerm,
          pagingOptions,
        );
      }),
      map((r: ListCampaignsV2Response) => {
        const campaignOptions = (r?.campaigns || []).map(this.toSimpleCampaign);
        if (extraData?.prependAnyOption) {
          campaignOptions.unshift(this.anyCampaignOption);
        }
        return <PagedResponse<SimpleCampaign>>{
          results: campaignOptions,
          nextCursor: r.metadata.nextCursor,
          hasMore: r.metadata.hasMore,
        };
      }),
    );
  }

  getMulti(campaignIds: string[]): Observable<SimpleCampaign[]> {
    if (campaignIds.length === 1 && campaignIds[0] === ANY_CAMPAIGN) {
      return of([this.anyCampaignOption]);
    }
    campaignIds = (campaignIds || []).filter((id) => id);
    if (campaignIds.length === 0) {
      return of([]);
    }
    return this.campaignsService.getMulti(campaignIds).pipe(
      map((campaigns) => {
        return campaigns.map(this.toSimpleCampaign);
      }),
    );
  }

  get(campaignId: string): Observable<SimpleCampaign> {
    return this.campaignsService.get(campaignId).pipe(map((c) => this.toSimpleCampaign(c)));
  }

  getCampaignEmailTemplate(templateId: string): Observable<TemplateData> {
    return this.lookupById([templateId]).pipe(map((templates) => templates[0]));
  }

  lookupById(templateIds: string[]): Observable<TemplateData[]> {
    let params = new HttpParams();
    templateIds.forEach((templateId) => {
      params = params.append('templateIds', templateId);
    });
    return this.http.get<{ data: TemplateData[] }>('/_ajax/v1/campaign/template/lookupById', { params }).pipe(
      map((response) => response.data),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  private toSimpleCampaign(c: GetterCampaignData): SimpleCampaign {
    return <SimpleCampaign>{
      id: c.campaignId,
      name: c.name,
      status: c.status,
    };
  }

  private setSenderType(): SenderType {
    switch (this.automationContext) {
      case Context.AUTOMATION_CONTEXT_PARTNER:
        return SenderType.SENDER_TYPE_PARTNER;
      case Context.AUTOMATION_CONTEXT_SMB:
        return SenderType.SENDER_TYPE_BUSINESS;
      default:
        return SenderType.SENDER_TYPE_PARTNER;
    }
  }
}
